import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import Modal from "../components/ui/Modal";
import Table from "components/ui/Table";
import AdminOverrideEdit from "components/adminOverride/Edit";
import {
  useAdminOverridesQuery,
  useAddAdminOverrideMutation,
  useUpdateAdminOverrideMutation,
  useDeleteAdminOverrideMutation,
} from "apis/adminOverride";

const TYPE_LABELS = {
  marketingApprove: "Marketing Approve",
  marketingDraft: "Marketing Draft",
  marketingPaid: "Marketing Paid",
  live: "Live",
  sold: "Sold",
  compliance: "Compliance",
};

const TYPE_COLORS = {
  marketingApprove: "bg-blue-100 text-blue-800",
  marketingDraft: "bg-indigo-100 text-indigo-800",
  marketingPaid: "bg-teal-100 text-teal-800",
  live: "bg-green-100 text-green-800",
  sold: "bg-yellow-100 text-yellow-800",
  compliance: "bg-purple-100 text-purple-800",
};

const AdminOverrides = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useAdminOverridesQuery();

  const { mutate: createOverride, isLoading: isCreateLoading } =
    useAddAdminOverrideMutation();
  const { mutate: updateOverride } = useUpdateAdminOverrideMutation();
  const { mutate: deleteOverride, isLoading: isDeleteLoading } =
    useDeleteAdminOverrideMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "propertyId.address",
        header: "Property",
        cell: (row) => (
          <span className="text-sm">{row.propertyId?.address}</span>
        ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              TYPE_COLORS[row.type] || "bg-gray-100 text-gray-800"
            }`}
          >
            {TYPE_LABELS[row.type] || row.type}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "notes",
        header: "Notes",
        cell: (row) => (
          <span className="text-sm text-darkgray line-clamp-2">
            {row.notes}
          </span>
        ),
        isFilter: true,
        minWidth: "240px",
      },
      {
        accessorKey: "overriddenBy.name",
        header: "By",
        cell: (row) => (
          <span className="text-sm">{row.overriddenBy?.name || "-"}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Override"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Override"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending) return <PageLoader />;

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading overrides: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedOverride = data?.find((o) => o._id === selectedId);

  const handleCreateSubmit = (formData) => {
    createOverride(formData);
    setIsAddOpen(false);
  };

  const handleEditSubmit = (formData) => {
    updateOverride({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleDelete = () => {
    deleteOverride(selectedId);
    setIsDeleteOpen(false);
  };

  const AddButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Override"
        className="max-w-2xl"
      >
        <AdminOverrideEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Override"
        className="max-w-2xl"
      >
        <AdminOverrideEdit
          initialData={selectedOverride || {}}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Override"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete this{" "}
            {TYPE_LABELS[selectedOverride?.type] || "override"}? This action
            cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Admin Overrides"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          searchPlaceholder="Search property, type, notes or admin..."
          emptyText="No overrides found"
          ButtonCmp={AddButton}
        />
      </div>
    </>
  );
};

export default AdminOverrides;
